import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  FormControl,
  Input,
  FormLabel,
  Textarea,
  Flex,
  useTheme,
  FormErrorMessage,
} from "@chakra-ui/react";
import SubmitButton from "../../buttons/SubmitButton";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";
import {
  createSubject,
  updateSubject,
} from "../../../store/slice/subjectSlice";
import { useDispatch } from "react-redux";

const AddSubjectModal = ({ isOpen, onClose, editData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();
  const { primaryBlueLight, primaryBlue } = theme.colors.pallete;
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    displayOrder: "",
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          name: editData.name || "",
          description: editData.description || "",
          displayOrder: editData.displayOrder || "",
        });
      } else {
        setFormData({ name: "", description: "", displayOrder: "" });
      }
      setFormErrors({});
    }
  }, [isOpen, editData]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        description: "",
        displayOrder: "",
      });
      setFormErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear the error when user types
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const validateForm = () => {
    let errors = {};
    if (!formData.name) errors.name = "Name is required.";
    if (!formData.description) errors.description = "Description is required.";

    const displayOrder = formData.displayOrder?.toString().trim();
    if (!displayOrder) {
      errors.displayOrder = "Display Order is required.";
    } else if (isNaN(displayOrder) || Number(displayOrder) <= 0) {
      errors.displayOrder = "Display Order must be a number.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const payload = {
      name: formData.name,
      description: formData.description,
      displayOrder: Number(formData.displayOrder),
    };
    setIsSubmitLoading(true);

    if (editData) {
      dispatch(updateSubject({ id: editData.id, formData }))
        .unwrap()
        .then((response) => {
          addNotification("Subject updated successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 3000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    } else {
      dispatch(createSubject(payload))
        .unwrap()
        .then((response) => {
          addNotification("Subject created successfully", "success", 3000);
          onClose();
        })
        .catch((error) => {
          addNotification(error, "warning", 4000);
        })
        .finally(() => {
          setIsSubmitLoading(false);
        });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={"18px"} fontWeight={400} textAlign="center">
          {editData ? "Update Subject" : "Add Subject"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired isInvalid={!!formErrors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              name="name"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter subject name"
              value={formData.name}
              onChange={handleChange}
            />
            <FormErrorMessage>{formErrors.name}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!formErrors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              borderColor={"gray.300"}
              fontSize={"14px"}
              placeholder="Enter description"
              value={formData.description}
              onChange={handleChange}
            />
            <FormErrorMessage>{formErrors.description}</FormErrorMessage>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!formErrors.displayOrder}>
            <FormLabel>Display Order</FormLabel>
            <Input
              name="displayOrder"
              fontSize={"14px"}
              borderColor={"gray.300"}
              placeholder="Enter display order"
              value={formData.displayOrder}
              onChange={handleChange}
            />
            <FormErrorMessage>{formErrors.displayOrder}</FormErrorMessage>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Flex w={"full"} justifyContent={"center"}>
            <SubmitButton
              text={editData ? "Update" : "Submit"}
              backColor={primaryBlue}
              textColor="white"
              hoverColor={primaryBlueLight}
              onClickHandler={handleSubmit}
              isLoading={isSubmitLoading}
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddSubjectModal;
